import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { useSelector } from 'modules/react-redux';
import { FC, memo, useMemo } from 'react';
import { useNetSuiteAttachmentsFieldSettings, useUserBetaFeatures } from 'shared/hooks';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { NetSuiteJournalEntryFooter } from '../components/NetSuiteJournalEntryFooter/NetSuiteJournalEntryFooter';
import { NetSuiteJournalEntryLines } from '../components/NetSuiteJournalEntryLines/NetSuiteJournalEntryLines';
import { useCurrencyPrecision, useExchangeRates, useUploadAttachments } from '../hooks';
import { isFieldMandatory, isFieldVisible } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { FieldsSection } from './components/FieldsSection/FieldsSection';
import {
    useGetActionsBlockProps,
    useNetSuiteJournalEntryAmounts,
    useNetSuiteJournalEntryLifecycles,
} from './NetSuiteJournalEntryRequestForm.hooks';
import { messages } from './NetSuiteJournalEntryRequestForm.messages';
import { netSuiteJournalEntryFieldValuesState } from './NetSuiteJournalEntryRequestForm.states';
import { Content, Root, Toolbar, ToolbarText } from './NetSuiteJournalEntryRequestForm.styles';
import { NetSuiteJournalEntryRequestFormProps } from './NetSuiteJournalEntryRequestForm.types';

export const NetSuiteJournalEntryRequestForm: FC<NetSuiteJournalEntryRequestFormProps> = memo((props) => {
    const { request } = props;

    const pageContext = useSelector(getContext);
    const editingContext = useSelector(getNetSuiteContext);
    const fieldValues = useAtomValue(netSuiteJournalEntryFieldValuesState);

    const { documentFields } = editingContext;

    const { onUploadAttachments } = useUploadAttachments(request.id);

    const { debitSubTotal, creditSubTotal, debitTotal, creditTotal, debitTotalTax, creditTotalTax } =
        useNetSuiteJournalEntryAmounts();

    const { data: dataExchangeRates } = useExchangeRates(request.companyId, fieldValues.transactionDate);

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency);

    const defaultCurrencyRate = useMemo(() => {
        const exchangeRate = dataExchangeRates?.rates.find((currency) => {
            return currency.id === fieldValues.currency?.id;
        });

        return exchangeRate?.rateValue;
    }, [dataExchangeRates, fieldValues.currency]);

    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);

    useNetSuiteJournalEntryLifecycles(request);

    const { getRequestTransfer, getValidationErrors } = useGetActionsBlockProps(
        request,
        defaultCurrencyRate,
        attachmentsFieldSettings
    );

    const { isBelgradeUIFilesUploadSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.toolbarText}</ToolbarText>

                    <ActionsBlock
                        customGetRequestTransfer={getRequestTransfer}
                        customRequestValidationFn={getValidationErrors}
                    />
                </Toolbar>

                <Content>
                    <Grid gridTemplateColumns='100%' padding='0 0 40'>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <FieldsSection workflowId={request.template.id} />

                        <NetSuiteJournalEntryLines workflowId={request.template.id} />

                        <NetSuiteJournalEntryFooter
                            currency={fieldValues.currency?.text ?? ''}
                            currencyPrecision={currencyPrecision}
                            debitTotal={debitTotal}
                            creditTotal={creditTotal}
                            debitTotalTax={debitTotalTax}
                            creditTotalTax={creditTotalTax}
                            debitSubTotal={debitSubTotal}
                            creditSubTotal={creditSubTotal}
                        />

                        {isFieldVisible(attachmentsFieldSettings.attachments) &&
                            (isBelgradeUIFilesUploadSection ? (
                                <Grid gridTemplateColumns='50%' gap={16}>
                                    <AttachmentsSection
                                        request={request}
                                        required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                    />
                                </Grid>
                            ) : (
                                <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />
                            ))}
                    </Grid>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

NetSuiteJournalEntryRequestForm.displayName = 'NetSuiteJournalEntryRequestForm';
